import { MeResponse } from "../types";
import { Icon, IconName } from "./Icon";
import { DOMAIN } from "../theme";

export type TabKey =
  | "tasks"
  | "payments"
  | "fx"
  | "exchange"
  | "analytics"
  | "audit"
  | "settings"
  | "health"
  | "search"
  | "rubinflow"
  | "agents"
  | "returns"
  | "subcontractors"
  | "backfill"
  | "key_analytics"
  | "client_cost"
  | "deals"
  | "trash"
  | "exports_import"
  | "closeout"
  | "test_cash_position"
  | "more";

interface Props {
  current: TabKey;
  onChange: (k: TabKey) => void;
  permissions: MeResponse["permissions"];
  badges?: Partial<Record<TabKey, number>>;
}

// Вкладки нижней панели. Вторичные разделы (Аналитика / Аудит /
// Профиль) скрыты под «Ещё», чтобы панель не была перегружена.
// У каждой вкладки — свой доменный акцент: активная вкладка
// подсвечивается им, что помогает мгновенно понять текущий раздел.
const BAR: Array<{
  key: TabKey;
  label: string;
  icon: IconName;
  accent: string;
  need?: keyof MeResponse["permissions"];
}> = [
  {
    key: "deals",
    label: "Заявки",
    icon: "list",
    accent: DOMAIN.payments,
    need: "payments_view",
  },
  
  {
    key: "payments",
    label: "Платежи",
    icon: "card",
    accent: DOMAIN.payments,
    need: "payments_view",
  },
  
  {
    key: "fx",
    label: "Экспорт",
    icon: "export",
    accent: DOMAIN.fx,
    need: "fx_view",
  },
  {
    key: "exchange",
    label: "Обмен",
    icon: "swap",
    accent: DOMAIN.exchange,
    need: "fx_view",
  },
  { key: "tasks", label: "Задачи", icon: "check", accent: DOMAIN.tasks },
  { key: "more", label: "Ещё", icon: "grid", accent: DOMAIN.more },
];

// Разделы, доступные через «Ещё».
export const SECONDARY_TABS: TabKey[] = [
  "analytics",
  "audit",
  "settings",
  "health",
  "search",
  "rubinflow",
  "agents",
  "returns",
  "subcontractors",
  "backfill",
  "key_analytics",
  "client_cost",
  "trash",
  "exports_import",
  "closeout",
  "test_cash_position",
];

export function TabBar({ current, onChange, permissions, badges }: Props) {
  const visible = BAR.filter((t) => !t.need || permissions[t.need]);
  // На вторичном разделе в панели подсвечиваем «Ещё».
  const activeKey: TabKey = SECONDARY_TABS.includes(current) ? "more" : current;
  return (
    <nav
      className="fixed bottom-0 left-0 right-0 bg-tg-headerBg border-t border-tg-secondaryBg"
      // Нижний отступ = вырез под home-indicator (env) ИЛИ минимум 8px —
      // чтобы на iPhone с закруглёнными углами иконки не уходили за край,
      // а на обычных телефонах меню всё равно было чуть приподнято.
      style={{
        paddingBottom: "max(env(safe-area-inset-bottom), 8px)",
      }}
    >
      <div className="max-w-3xl mx-auto flex">
      {visible.map((t) => {
        const badge = badges?.[t.key] ?? 0;
        const active = activeKey === t.key;
        return (
          <button
            key={t.key}
            onClick={() => onChange(t.key)}
            className={
              "flex-1 pt-2 pb-1.5 flex flex-col items-center text-xs " +
              (active ? "font-medium" : "text-tg-hint")
            }
            style={active ? { color: t.accent } : undefined}
          >
            <span className="relative leading-none">
              <Icon name={t.icon} size={22} />
              {badge > 0 && (
                <span className="absolute -top-1.5 -right-2.5 min-w-[16px] h-4 px-1 rounded-full bg-tg-destructive text-white text-[10px] font-bold flex items-center justify-center leading-none">
                  {badge > 99 ? "99+" : badge}
                </span>
              )}
            </span>
            <span className="mt-1">{t.label}</span>
          </button>
        );
      })}
      </div>
    </nav>
  );
}
